import { ReloadOutlined } from '@ant-design/icons'
import { Button, Form, FormInstance, Input, Select, Spin } from 'antd'
import { t } from 'i18next'
import { useCallback, useState } from 'react'
import { SelectAnnotationType, SelectSource } from '../../../../components'
import { selectSourcePrivacyTypes } from '../../../../components/SelectSource'
import {
  OfrTemplateType,
  ofrTemplateTypes,
} from '../../../../constants/constants'
import { useDataSources } from '../../../../hooks'
import { OfrFormValues } from '../CreateOfrDocumentTemplateModal'
import { DataSource } from '../../../../store/reducers/dataSourceReducer'

interface SelectOption {
  label: string
  value: string
}

interface CreateOfrDocumentTemplateModalStepProps {
  form: FormInstance<OfrFormValues>
}

const initialState = {
  documents: [] as SelectOption[],
  selectedDocument: undefined as string | undefined,
  templateType: ofrTemplateTypes.QUALITY as OfrTemplateType,
}

const CreateOfrDocumentTemplateModalStep = ({
  form,
}: CreateOfrDocumentTemplateModalStepProps) => {
  const { getDataSourceFiles } = useDataSources({ preventFetch: true })

  const [loading, setLoading] = useState(false)
  const [state, setState] = useState(initialState)

  const setFormFields = useCallback(
    (fields: Partial<OfrFormValues>) => {
      form.setFieldsValue(fields)
    },
    [form]
  )

  const handleSourceChange = (dataSource: DataSource | undefined) => {
    setFormFields({
      dataSource: dataSource?.id,
      documentId: undefined,
      templateType: ofrTemplateTypes.QUALITY,
    })
    setState(initialState)
  }

  const handleDocumentChange = (documentId: string) => {
    setFormFields({ documentId })
    setState((prev) => ({ ...prev, selectedDocument: documentId }))
  }

  const handleTemplateTypeChange = (templateType: string) => {
    setFormFields({ templateType: templateType as OfrTemplateType })
    setState((prev) => ({
      ...prev,
      templateType: templateType as OfrTemplateType,
    }))
  }

  const fetchDocuments = useCallback(async () => {
    const dataSourceId = form.getFieldValue('dataSource')
    if (!dataSourceId) return

    setLoading(true)
    setFormFields({ documentId: undefined })
    setState((prev) => ({
      ...prev,
      documents: [],
      selectedDocument: undefined,
    }))

    try {
      const files = await getDataSourceFiles({ dataSourceId, format: 'pdf' })
      if (files) {
        const documents = files.map((file: any) => ({
          label: file.path,
          value: file.id,
        }))
        setState((prev) => ({ ...prev, documents }))
      }
    } catch (err) {
      console.error('Error fetching documents:', err)
    } finally {
      setLoading(false)
    }
  }, [form, getDataSourceFiles, setFormFields])

  return (
    <Spin spinning={loading}>
      <Form.Item
        name="title"
        label={t('createOfrDocumentTemplateModal.titleDescription')}
        rules={[
          {
            required: true,
            message: t('createOfrDocumentTemplateModal.titleRequired'),
          },
        ]}
      >
        <Input
          placeholder={t('createOfrDocumentTemplateModal.titlePlaceholder')}
        />
      </Form.Item>

      <Form.Item
        name="dataSource"
        label={t('createOfrDocumentTemplateModal.dataSourceDescription')}
        rules={[
          {
            required: true,
            message: t('createOfrDocumentTemplateModal.dataSourceRequired'),
          },
        ]}
      >
        <SelectSource
          onChange={handleSourceChange}
          privacy={selectSourcePrivacyTypes.INTERNAL}
          includeTypeCode={false}
          includeSearchable={false}
        />
      </Form.Item>

      <Form.Item
        name="templateType"
        label={t('createOfrDocumentTemplateModal.chooseTemplate')}
        rules={[
          {
            required: true,
            message: t('createOfrDocumentTemplateModal.chooseTemplateRequired'),
          },
        ]}
      >
        <SelectAnnotationType
          value={state.templateType}
          onChange={handleTemplateTypeChange}
        />
      </Form.Item>

      <Form.Item
        name="documentId"
        label={t('createOfrDocumentTemplateModal.chooseTemplateDescription')}
        rules={[
          {
            required: true,
            message: t('createOfrDocumentTemplateModal.documentRequired'),
          },
        ]}
        extra={t('createOfrDocumentTemplateModal.chooseTemplateSubheader')}
      >
        <Select
          value={state.selectedDocument}
          placeholder={t(
            'createOfrDocumentTemplateModal.chooseTemplatePlaceholder'
          )}
          onChange={handleDocumentChange}
          options={state.documents}
        />
      </Form.Item>

      <Button onClick={fetchDocuments} icon={<ReloadOutlined />} block>
        {t('createOfrDocumentTemplateModal.refreshButton')}
      </Button>
    </Spin>
  )
}

export default CreateOfrDocumentTemplateModalStep
